import { useCallback, useEffect, useRef, useState } from 'react';
import { unlockApp, unlockName } from './appLock';

// Covers the app until the device's unlock succeeds. It asks straight away; if the browser won't start it without a
// tap (or the person cancels), the button is there to try again. Signing out is the way past it without the unlock.
export function LockScreen({ onUnlocked, onSignOut }: { onUnlocked: () => void; onSignOut: () => void }) {
  const [trying, setTrying] = useState(false);
  const [failed, setFailed] = useState(false);
  const pending = useRef<AbortController | null>(null);
  const name = unlockName();

  const attempt = useCallback(async () => {
    pending.current?.abort();
    const ctl = new AbortController();
    pending.current = ctl;
    setTrying(true); setFailed(false);
    try {
      if (await unlockApp(ctl.signal)) { onUnlocked(); return; }
      if (!ctl.signal.aborted) setFailed(true);
    } catch {
      if (!ctl.signal.aborted) setFailed(true);
    } finally {
      if (pending.current === ctl) { pending.current = null; setTrying(false); }
    }
  }, [onUnlocked]);

  useEffect(() => {
    void attempt();
    return () => pending.current?.abort();
  }, [attempt]);

  return (
    <div className="app-lock" role="dialog" aria-modal="true" aria-label="Elevations Planning is locked">
      <div className="app-lock-hero">
        <div className="boot-icon"><img src="./assets/loading-icon.png" alt="" /></div>
        <div className="app-lock-title">Elevations Planning is locked</div>
      </div>
      {/* At the bottom, where the system's own unlock sheet slides up over it. */}
      <div className="app-lock-actions">
        {failed && !trying && <div className="app-lock-note">Not unlocked. Try again, or sign out and sign in with your password.</div>}
        {/* Never disabled: if a request started without a tap just hangs, tapping starts a fresh one. */}
        <button type="button" className="btn btn-primary app-lock-btn" onClick={() => void attempt()}>
          Unlock with {name}
        </button>
        <button type="button" className="sx-link" onClick={onSignOut}>Sign out</button>
      </div>
    </div>
  );
}
